import SelectBuscable from '@/shared/components/SelectBuscable'
import { TituloDePantalla } from '@/shared/components/TituloDePantalla'
import React, { useState, useEffect } from 'react';
import { ArrowLeft, Download, Search, Calendar, Loader2, Plus, CheckCircle } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { purchaseOrdersRHService } from '@/features/rh/api/purchaseOrdersRH.service';
import type { Payment, Invoice } from '@/types/purchaseOrderRH.types';
import { AddPaymentModal, type PaymentFormData } from '@/features/rh/components/rh/AddPaymentModal';
import { useAuth } from '@/shared/hooks/useAuth';
import { userHasPermission } from '@/shared/lib/roleUtils';
import { nombreDeUsuario } from '@/shared/lib/nombreDeUsuario';
import PermissionsDenyPage from '@/pages/configuracion/roles/permissionsDenyPage';
import { Badge } from '@/shared/components/Badge';
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton';

export const PurchaseOrderPayments: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { orderId } = useParams<{ orderId: string }>();
  const [searchTerm, setSearchTerm] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [payments, setPayments] = useState<Payment[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchPayments = async () => {
      if (!orderId) return;

      try {
        setLoading(true);
        setError(null);
        const [paymentsData, invoicesData] = await Promise.all([
          purchaseOrdersRHService.getAllPayments(orderId),
          purchaseOrdersRHService.getInvoices(orderId)
        ]);
        setPayments(paymentsData);
        setInvoices(invoicesData);
      } catch (errCrudo: unknown) {
        const err = errCrudo as {
          message?: string;
          statusCode?: number;
          status?: number;
          error?: string;
          response?: { status?: number; data?: { message?: string } };
        };
        console.error('Error fetching payments:', err);
        setError(err.response?.data?.message || 'Error al cargar los pagos');
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, [orderId]);

  const handleAddPayment = async (data: PaymentFormData) => {
    if (!orderId) return;

    const ids = data.invoiceIds?.length ? data.invoiceIds : (data.invoiceId ? [data.invoiceId] : []);
    if (ids.length === 0) {
      throw new Error('Agrega al menos una factura');
    }

    await purchaseOrdersRHService.createBatchPayment({
      invoiceIds: ids,
      amount: data.amount,
      payment_date: data.payment_date,
      payment_method: data.payment_method,
      reference_number: data.reference_number,
      notes: data.notes,
      receipt: data.receipt,
    });

    setSuccess(true);
    setTimeout(() => setSuccess(false), 3000);

    const updatedPayments = await purchaseOrdersRHService.getAllPayments(orderId);
    setPayments(updatedPayments);
  };

  const filteredPayments = payments.filter(payment =>
    payment.supplier_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    payment.supplier_rfc?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    payment.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (
    userHasPermission(
      'proveedores.administracion.ordenes_compra.consultar',
      user?.rbac?.effectivePermissions || []
    ) === false
  ) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <PermissionsDenyPage />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <button
            onClick={() => navigate(`/rh/purchase-orders/${orderId}`)}
            className={estiloDeBoton('secundario', 'mb-3')}
          >
            <ArrowLeft className="w-4 h-4" />
            Volver a órdenes de compra
          </button>
          <TituloDePantalla className="mb-2">
            Pagos
          </TituloDePantalla>
          <p className="text-gray-600 dark:text-gray-400">
            Gestión de pagos de la orden de compra
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setIsModalOpen(true)}
            className={estiloDeBoton('primario')}
          >
            <Plus className="w-4 h-4" />
            Agregar pago
          </button>
          <button className={estiloDeBoton('secundario')}>
            <Download className="w-4 h-4" />
            Exportar a excel
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap gap-3">
          {/* Search */}
          <div className="flex-1 min-w-[250px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar en orden de compra..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Proveedor Dropdown */}
          <div className="relative">
            <SelectBuscable
              variante="listado"
              ancho="contenido"
              etiqueta="Proveedor"
              todos="Todos"
              value={supplierFilter}
              onChange={setSupplierFilter}
              placeholderDeBusqueda="Buscar…"
              canDelete={false}
              opciones={[]}
            />
          </div>

          {/* Estado Dropdown */}
          <div className="relative">
            <SelectBuscable
              variante="listado"
              ancho="contenido"
              etiqueta="Estado"
              todos="Todos"
              value={statusFilter}
              onChange={setStatusFilter}
              placeholderDeBusqueda="Buscar…"
              canDelete={false}
              opciones={[]}
            />
          </div>

          {/* Date From */}
          <div className="relative">
            <input
              type="text"
              placeholder="mm/dd/aaaa"
              className="w-40 pl-3 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-500" />
          </div>

          {/* Date To */}
          <div className="relative">
            <input
              type="text"
              placeholder="mm/dd/aaaa"
              className="w-40 pl-3 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-500" />
          </div>

          {/* Clear Button */}
          <button className="elevar px-4 py-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg font-medium transition-colors">
            Limpiar
          </button>
        </div>
      </div>

      {/* Payments Table */}
      <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-[#E5E7EB]">
            <thead className="bg-[#FAF9FF]">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                  Factura
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                  Monto
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                  Fecha de pago
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                  Método
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                  Referencia
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                  Complemento
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                  Registrado por
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-[#E1E1E1]">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Cargando pagos...</span>
                    </div>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <div className="text-red-600 dark:text-red-400">
                      {error}
                    </div>
                  </td>
                </tr>
              ) : filteredPayments.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <div className="text-gray-500 dark:text-gray-400">
                      {payments.length === 0
                        ? 'No hay facturas registradas para esta orden'
                        : 'No se encontraron pagos con los filtros aplicados'
                      }
                    </div>
                  </td>
                </tr>
              ) : (
                filteredPayments.map((payment) => (
                  <tr
                    key={payment.id}
                    className="group hover:bg-[#F4F7FB] transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {payment.invoice_number || invoices.find(i => i.id === payment.invoice_id)?.supplier_invoice_number || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-white">
                      ${payment.amount.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {purchaseOrdersRHService.formatDate(payment.payment_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {payment.payment_method || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white font-mono">
                      {payment.reference_number || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge color={payment.invoice_status === 'approved' ? 'verde' : 'amarillo'}>
                        {payment.invoice_status === 'approved' ? 'Pagado' : 'Pendiente'}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex flex-col gap-2">
                        {payment.complement_status === 'rejected' && (
                          <div className="mb-1">
                            <Badge color="rojo" className="mb-1">Rechazado</Badge>
                            {payment.complement_validation_notes && (
                              <p className="text-xs text-red-600 dark:text-red-400 max-w-[200px] whitespace-normal">
                                {payment.complement_validation_notes}
                              </p>
                            )}
                          </div>
                        )}

                        {(payment.complement_xml_url || payment.complement_pdf_url) ? (
                          <div className="flex items-center gap-2">
                            {payment.complement_xml_url && (
                              <a
                                href={payment.complement_xml_url}
                                download
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-blue-900/50 rounded transition-colors"
                                title="Descargar XML"
                              >
                                <Download className="w-3 h-3" />
                                XML
                              </a>
                            )}
                            {payment.complement_pdf_url && (
                              <a
                                href={payment.complement_pdf_url}
                                download
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/50 rounded transition-colors"
                                title="Descargar PDF"
                              >
                                <Download className="w-3 h-3" />
                                PDF
                              </a>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">Sin complemento</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {nombreDeUsuario(payment.createdBy ?? payment.createdByHistorico)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Success notification */}
      {
        success && (
          <div className="fixed bottom-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 animate-fade-in z-50">
            <CheckCircle className="w-5 h-5" />
            Pago agregado exitosamente
          </div>
        )
      }

      {/* Add Payment Modal */}
      <AddPaymentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleAddPayment}
        invoices={invoices.filter(i => i.status === 'approved')}
      />
    </div >
  );
};
